// Renders an integer that tweens from its previous value to the new value
// when the prop changes. The difference between "spreadsheet" and "game"
// is exactly this animation — HP 50 → 47 should feel like 3 damage points
// being subtracted, not a snap.
//
// Usage:
//   <TweenedNumber value={char.currentHp} />
//
// Falls back to instant snap when prefers-reduced-motion is set.
//
// Implementation note: we hold the displayed value in state so React
// re-renders during the tween. The animation uses requestAnimationFrame
// with an ease-out cubic curve over `durationMs` milliseconds.

import React, { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';

export default function TweenedNumber({ value, durationMs = 220 }) {
  const reduceMotion = useReducedMotion();
  const [displayed, setDisplayed] = useState(value);
  const fromRef = useRef(value);
  const targetRef = useRef(value);
  const startRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (value === targetRef.current) return;
    if (reduceMotion) {
      setDisplayed(value);
      targetRef.current = value;
      fromRef.current = value;
      return;
    }
    fromRef.current = displayed;
    targetRef.current = value;
    startRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const animate = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setDisplayed(t === 1 ? targetRef.current : Math.round(v));
      if (t < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, reduceMotion, durationMs]);

  return <>{displayed}</>;
}
